import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { getMlRedirectUri } from '@/lib/ml';

/**
 * Recebe o `code` do redirect do Mercado Livre, encaminha pra Edge Function
 * `ml-oauth-callback` (que cifra os tokens) e redireciona ao dashboard.
 *
 * O `code` vai pra função via header de autorização do usuário Supabase já logado;
 * a função usa o service role internamente para gravar no banco.
 */
export function OAuthCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState<'loading' | 'ok' | 'error'>('loading');
  const [message, setMessage] = useState<string>('Concluindo conexão com o Mercado Livre…');

  useEffect(() => {
    const code = params.get('code');
    const error = params.get('error');

    if (error) {
      setState('error');
      setMessage(`Mercado Livre retornou um erro: ${error}`);
      return;
    }
    if (!code) {
      setState('error');
      setMessage('Código de autorização ausente no redirect. Tente conectar novamente.');
      return;
    }

    (async () => {
      try {
        const codeVerifier = sessionStorage.getItem('ml_pkce_verifier') ?? undefined;
        sessionStorage.removeItem('ml_pkce_verifier');
        // Reusa a MESMA redirect_uri enviada na autorização (salva pelo connectML).
        // Se não existir (ex.: aba nova), recalcula pela origem atual.
        const redirectUri = sessionStorage.getItem('ml_redirect_uri') ?? getMlRedirectUri();
        sessionStorage.removeItem('ml_redirect_uri');
        if (!codeVerifier) {
          throw new Error(
            'code_verifier ausente: o redirect voltou para uma origem diferente da que iniciou a conexão (ex.: iniciou no localhost e voltou em produção, ou vice-versa). Verifique se a redirect_uri cadastrada no Dev Center corresponde ao ambiente em que você está testando e tente novamente.'
          );
        }
        const { data, error } = await supabase.functions.invoke('ml-oauth-callback', {
          body: {
            code,
            redirect_uri: redirectUri,
            code_verifier: codeVerifier,
          },
        });
        if (error) {
          // O FunctionsHttpError do supabase-js vem com mensagem genérica
          // ("non-2xx status code"); o motivo real está no corpo da resposta.
          let detail = error.message as string;
          const ctx = (error as { context?: Response }).context;
          if (ctx && typeof ctx.json === 'function') {
            try {
              const body = await ctx.json();
              if (body?.error) detail = `${body.error} (HTTP ${ctx.status})`;
            } catch { /* corpo não era JSON; mantém mensagem genérica */ }
          }
          throw new Error(detail);
        }
        if (!data?.ok) throw new Error(data?.error ?? 'Falha ao trocar code por token.');

        setState('ok');
        setMessage('Loja conectada com sucesso. Sincronizando pedidos…');
        // Backfill inicial: sincroniza os últimos 30 dias de pedidos.
        // Fire-and-forget — o cron de 5 min mantém atualizado depois.
        void supabase.functions.invoke('ml-sync-orders', { body: { days: 30 } });
        setTimeout(() => navigate('/logistica', { replace: true }), 1500);
      } catch (e) {
        setState('error');
        setMessage((e as Error).message);
      }
    })();
  }, [navigate, params]);

  return (
    <div className="min-h-full flex items-center justify-center p-6">
      <div className="card-pad max-w-md w-full text-center">
        <div className="mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-4">
          {state === 'loading' && <Loader2 className="w-8 h-8 text-brand-700 animate-spin" />}
          {state === 'ok' && <CheckCircle2 className="w-8 h-8 text-positive" />}
          {state === 'error' && <AlertTriangle className="w-8 h-8 text-negative" />}
        </div>
        <h2 className="font-display text-lg font-bold">Mercado Livre</h2>
        <p className="text-sm text-ink-muted mt-2">{message}</p>
        {state === 'error' && (
          <button onClick={() => navigate('/logistica', { replace: true })} className="btn-secondary mt-6">
            Voltar ao painel
          </button>
        )}
      </div>
    </div>
  );
}
